import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Container,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Payment,
  Business,
  Star,
  Security,
  CreditCard,
  Receipt
} from '@mui/icons-material';
import { useAuth } from '../services/auth';

const SubscriptionManager = ({ user, onSubscriptionUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('professional');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);
  const [promoCodeError, setPromoCodeError] = useState('');
  const [showPromoSection, setShowPromoSection] = useState(false);

  const subscriptionPlans = [
    {
      id: 'free',
      name: 'Free',
      price: '$0',
      features: [
        '2 Social Media Accounts',
        'Basic Analytics',
        'Limited AI Content',
        'Email Support'
      ],
      limitations: [
        'No Ad Campaign Management',
        'No Advanced Analytics',
        'No Priority Support'
      ]
    },
    {
      id: 'professional',
      name: 'Professional',
      price: '$79/month',
      features: [
        '5 Social Media Accounts',
        'Advanced Analytics',
        'AI Content Generation',
        'Ad Campaign Management',
        'Priority Support',
        'Lead Capture System'
      ],
      popular: true
    },
    {
      id: 'premium',
      name: 'Premium',
      price: '$199/month',
      features: [
        'Unlimited Social Accounts',
        'Full Analytics Suite',
        'Advanced AI Content',
        'White-label Options',
        'Dedicated Support',
        'Custom Integrations'
      ]
    }
  ];

  const handlePlanSelection = (planId) => {
    setSelectedPlan(planId);
  };

  const handlePromoCodeApply = async () => {
    if (!promoCode.trim()) {
      setPromoCodeError('Please enter a promo code');
      return;
    }

    setLoading(true);
    setPromoCodeError('');

    try {
      // Simulate promo code validation
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Valid promo codes for testing
      const validPromoCodes = {
        'FREETRIAL30': { type: 'trial', days: 30, discount: 0 },
        'WELCOME50': { type: 'discount', discount: 50 },
        'LAUNCH2024': { type: 'trial', days: 14, discount: 0 },
        'TESTCODE': { type: 'trial', days: 7, discount: 0 }
      };

      const promoData = validPromoCodes[promoCode.toUpperCase()];
      
      if (promoData) {
        setPromoCodeApplied(true);
        setPromoCodeError('');
        
        // Apply promo code benefits
        if (promoData.type === 'trial') {
          const trialEndsAt = new Date();
          trialEndsAt.setDate(trialEndsAt.getDate() + promoData.days);
          
          const updatedUser = {
            ...user,
            user_metadata: {
              ...user.user_metadata,
              subscription_tier: selectedPlan,
              subscription_status: 'trial',
              payment_verified: true,
              trial_ends_at: trialEndsAt.toISOString(),
              promo_code_applied: promoCode.toUpperCase(),
              trial_days: promoData.days
            }
          };
          
          onSubscriptionUpdated(updatedUser);
        }
      } else {
        setPromoCodeError('Invalid promo code. Please try again.');
      }
    } catch (error) {
      setPromoCodeError('Error applying promo code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePromoCode = () => {
    setPromoCodeApplied(false);
    setPromoCode('');
    setPromoCodeError('');
  };

  const handlePaymentVerification = async () => {
    setLoading(true);
    
    try {
      // Simulate payment verification
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Update user subscription in database
      const updatedUser = {
        ...user,
        user_metadata: {
          ...user.user_metadata,
          subscription_tier: selectedPlan,
          subscription_status: 'active',
          payment_verified: true,
          payment_verified_at: new Date().toISOString()
        }
      };

      // Call callback to update user
      onSubscriptionUpdated(updatedUser);
      
    } catch (error) {
      console.error('Payment verification failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    setLoading(true);
    
    try {
      // Simulate upgrade process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const updatedUser = {
        ...user,
        user_metadata: {
          ...user.user_metadata,
          subscription_tier: selectedPlan,
          subscription_status: 'active',
          payment_verified: true,
          upgraded_at: new Date().toISOString()
        }
      };

      onSubscriptionUpdated(updatedUser);
      
    } catch (error) {
      console.error('Upgrade failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentPlan = subscriptionPlans.find(plan => plan.id === (user?.user_metadata?.subscription_tier || 'free'));

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Subscription Management
        </Typography>
        
        <Paper sx={{ p: 3, mt: 3 }}>
          <Grid container spacing={3}>
            {/* Current Plan */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Current Plan: {currentPlan?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Status: {user?.user_metadata?.subscription_status || 'inactive'}
                  </Typography>
                  {user?.user_metadata?.payment_verified ? (
                    <Chip 
                      icon={<CheckCircle />} 
                      label="Payment Verified" 
                      color="success" 
                      variant="outlined" 
                    />
                  ) : (
                    <Chip 
                      icon={<Warning />} 
                      label="Payment Required" 
                      color="warning" 
                      variant="outlined" 
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Plan Selection */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Choose Your Plan
              </Typography>
              <Grid container spacing={2}>
                {subscriptionPlans.map((plan) => (
                  <Grid item xs={12} md={4} key={plan.id}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        border: selectedPlan === plan.id ? 2 : 1,
                        borderColor: selectedPlan === plan.id ? 'primary.main' : 'divider',
                        position: 'relative'
                      }}
                      onClick={() => handlePlanSelection(plan.id)}
                    >
                      {plan.popular && (
                        <Chip
                          label="Most Popular"
                          color="primary"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1
                          }}
                        />
                      )}
                      <CardContent>
                        <Typography variant="h5" gutterBottom>
                          {plan.name}
                        </Typography>
                        <Typography variant="h4" color="primary" gutterBottom>
                          {plan.price}
                        </Typography>
                        
                        <List dense>
                          {plan.features.map((feature, index) => (
                            <ListItem key={index} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckCircle color="primary" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary={feature} />
                            </ListItem>
                          ))}
                          {plan.limitations && plan.limitations.map((limitation, index) => (
                            <ListItem key={index} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <Warning color="disabled" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText 
                                primary={limitation} 
                                sx={{ color: 'text.disabled' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* Promo Code Section */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <Receipt sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Promo Code
                  </Typography>
                  
                  {!promoCodeApplied ? (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Have a promo code? Enter it to get a free trial or discount.
                      </Typography>
                      
                      <Grid container spacing={2} alignItems="flex-end">
                        <Grid item xs={12} sm={8}>
                          <TextField
                            fullWidth
                            label="Promo Code"
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value)}
                            placeholder="Enter promo code"
                            error={!!promoCodeError}
                            helperText={promoCodeError}
                            disabled={loading}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handlePromoCodeApply}
                            disabled={loading || !promoCode.trim()}
                            fullWidth
                          >
                            {loading ? <CircularProgress size={20} /> : 'Apply'}
                          </Button>
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          <strong>Test Codes:</strong> FREETRIAL30 (30-day trial), LAUNCH2024 (14-day trial), TESTCODE (7-day trial)
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box>
                      <Alert severity="success" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          ✅ Promo code applied successfully! You now have access to the platform.
                        </Typography>
                      </Alert>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleRemovePromoCode}
                      >
                        Remove Promo Code
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Payment Information */}
            {selectedPlan !== 'free' && !promoCodeApplied && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <CreditCard sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Payment Information
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Card Number"
                          value={cardNumber}
                          onChange={(e) => setCardNumber(e.target.value)}
                          placeholder="1234 5678 9012 3456"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Expiry Date"
                          value={expiryDate}
                          onChange={(e) => setExpiryDate(e.target.value)}
                          placeholder="MM/YY"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="CVV"
                          value={cvv}
                          onChange={(e) => setCvv(e.target.value)}
                          placeholder="123"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                {selectedPlan === 'free' ? (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handlePaymentVerification}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Activate Free Plan'}
                  </Button>
                ) : promoCodeApplied ? (
                  <Alert severity="success" sx={{ width: '100%' }}>
                    <Typography variant="body1">
                      ✅ Promo code applied! You now have access to the platform.
                    </Typography>
                  </Alert>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={handleUpgrade}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : `Upgrade to ${subscriptionPlans.find(p => p.id === selectedPlan)?.name}`}
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={handlePaymentVerification}
                      disabled={loading}
                    >
                      Verify Payment Only
                    </Button>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
};

export default SubscriptionManager;
